import React, { useState } from 'react'
import { Download, FileSpreadsheet, FileText, Table } from 'lucide-react'
import { Button } from '@/components/ui/button'
import * as XLSX from 'xlsx'

interface Asset {
  id: string
  fileName: string
  format: string
  dimensions?: { width: number; height: number }
  fileSize: number
  thumbnailUrl?: string
  previewUrl?: string
  channel: string
  indication: string
  customerType: string
  complianceScore: number
}

interface ExportMatrixProps {
  assets: Asset[]
  campaignName: string
}

export default function ExportMatrix({ assets, campaignName }: ExportMatrixProps) {
  const [exporting, setExporting] = useState(false)

  const exportToCSV = () => {
    const headers = [
      'Asset Name',
      'Format',
      'Dimensions',
      'File Size (KB)',
      'Channel',
      'Indication',
      'Customer Type',
      'Compliance Score',
      'Preview URL'
    ]

    const rows = assets.map(asset => [
      asset.fileName,
      asset.format,
      asset.dimensions ? `${asset.dimensions.width}x${asset.dimensions.height}` : 'N/A',
      (asset.fileSize / 1024).toFixed(2),
      asset.channel,
      asset.indication,
      asset.customerType,
      asset.complianceScore,
      asset.previewUrl || ''
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    downloadFile(csvContent, `${campaignName}_matrix.csv`, 'text/csv')
  }

  const exportToHTML = async () => {
    setExporting(true)

    // Create HTML with embedded images
    const html = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${campaignName} - Creative Matrix</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            padding: 40px;
            background: #f5f5f5;
        }
        .header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 30px;
            border-radius: 12px;
            margin-bottom: 30px;
        }
        .matrix {
            background: white;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }
        table {
            width: 100%;
            border-collapse: collapse;
        }
        th {
            background: #f8f9fa;
            padding: 16px;
            text-align: left;
            font-weight: 600;
            border-bottom: 2px solid #dee2e6;
        }
        td {
            padding: 16px;
            border-bottom: 1px solid #dee2e6;
        }
        .asset-preview {
            width: 120px;
            height: 90px;
            object-fit: cover;
            border-radius: 8px;
            border: 2px solid #e9ecef;
        }
        .compliance-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 600;
        }
        .compliance-high { background: #d4edda; color: #155724; }
        .compliance-medium { background: #fff3cd; color: #856404; }
        .compliance-low { background: #f8d7da; color: #721c24; }
        .footer {
            text-align: center;
            margin-top: 30px;
            color: #6c757d;
            font-size: 14px;
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>${campaignName}</h1>
        <p>Creative Matrix - Generated on ${new Date().toLocaleDateString()}</p>
        <p>Total Assets: ${assets.length}</p>
    </div>
    
    <div class="matrix">
        <table>
            <thead>
                <tr>
                    <th>Preview</th>
                    <th>Asset Name</th>
                    <th>Format</th>
                    <th>Dimensions</th>
                    <th>Size</th>
                    <th>Channel</th>
                    <th>Indication</th>
                    <th>Customer</th>
                    <th>Compliance</th>
                </tr>
            </thead>
            <tbody>
                ${assets.map(asset => `
                    <tr>
                        <td>
                            ${asset.previewUrl ? 
                                `<img src="${asset.previewUrl}" class="asset-preview" alt="${asset.fileName}" />` : 
                                '<div class="asset-preview" style="background:#e9ecef;display:flex;align-items:center;justify-content:center">No Preview</div>'
                            }
                        </td>
                        <td><strong>${asset.fileName}</strong></td>
                        <td>${asset.format}</td>
                        <td>${asset.dimensions ? `${asset.dimensions.width}×${asset.dimensions.height}` : 'N/A'}</td>
                        <td>${(asset.fileSize / 1024).toFixed(0)} KB</td>
                        <td>${asset.channel}</td>
                        <td>${asset.indication}</td>
                        <td>${asset.customerType}</td>
                        <td>
                            <span class="compliance-badge compliance-${
                                asset.complianceScore >= 80 ? 'high' : 
                                asset.complianceScore >= 60 ? 'medium' : 'low'
                            }">
                                ${asset.complianceScore}%
                            </span>
                        </td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    </div>
    
    <div class="footer">
        <p>Generated by DataRaiils - Creative Operations Platform</p>
        <p>© ${new Date().getFullYear()} All rights reserved</p>
    </div>
</body>
</html>
    `

    downloadFile(html, `${campaignName}_matrix.html`, 'text/html')
    setExporting(false)
  }

  const exportToExcel = async () => {
    setExporting(true)

    try {
      // Create workbook
      const wb = XLSX.utils.book_new()

      // Create worksheet data
      const wsData = [
        // Header row
        ['Asset Name', 'Format', 'Dimensions', 'File Size (KB)', 'Channel', 'Indication', 'Customer Type', 'Compliance Score', 'Status'],
        // Data rows
        ...assets.map(asset => [
          asset.fileName,
          asset.format,
          asset.dimensions ? `${asset.dimensions.width}x${asset.dimensions.height}` : 'N/A',
          (asset.fileSize / 1024).toFixed(2),
          asset.channel,
          asset.indication,
          asset.customerType,
          asset.complianceScore,
          asset.complianceScore >= 80 ? 'Compliant' : asset.complianceScore >= 60 ? 'Review' : 'Non-Compliant'
        ])
      ]

      const ws = XLSX.utils.aoa_to_sheet(wsData)

      // Set column widths
      ws['!cols'] = [
        { wch: 30 }, // Asset Name
        { wch: 10 }, // Format
        { wch: 12 }, // Dimensions
        { wch: 12 }, // File Size
        { wch: 12 }, // Channel
        { wch: 20 }, // Indication
        { wch: 12 }, // Customer Type
        { wch: 12 }, // Compliance
        { wch: 15 }  // Status
      ]

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Creative Matrix')

      // Create summary sheet
      const summary = [
        ['Campaign Summary'],
        ['Campaign Name', campaignName],
        ['Total Assets', assets.length],
        ['Generated Date', new Date().toLocaleDateString()],
        [''],
        ['Compliance Breakdown'],
        ['Fully Compliant (80%+)', assets.filter(a => a.complianceScore >= 80).length],
        ['Needs Review (60-79%)', assets.filter(a => a.complianceScore >= 60 && a.complianceScore < 80).length],
        ['Non-Compliant (<60%)', assets.filter(a => a.complianceScore < 60).length],
        [''],
        ['By Channel'],
        ...Object.entries(
          assets.reduce((acc, asset) => {
            acc[asset.channel] = (acc[asset.channel] || 0) + 1
            return acc
          }, {} as Record<string, number>)
        )
      ]

      const wsSummary = XLSX.utils.aoa_to_sheet(summary)
      wsSummary['!cols'] = [{ wch: 30 }, { wch: 20 }]
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary')

      // Generate Excel file
      XLSX.writeFile(wb, `${campaignName}_matrix.xlsx`)

    } catch (error) {
      console.error('Failed to export Excel:', error)
      alert('Failed to export Excel file. Please try CSV format.')
    } finally {
      setExporting(false)
    }
  }

  const downloadFile = (content: string, filename: string, type: string) => {
    const blob = new Blob([content], { type })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      <Download className="w-5 h-5 text-primary" />
      <span className="font-medium">Export Creative Matrix:</span>
      
      <Button
        variant="outline"
        size="sm"
        onClick={exportToCSV}
        disabled={exporting || assets.length === 0}
        className="flex items-center gap-2"
      >
        <Table className="w-4 h-4" />
        CSV
      </Button>

      <Button
        variant="outline"
        size="sm"
        onClick={exportToHTML}
        disabled={exporting || assets.length === 0}
        className="flex items-center gap-2"
      >
        <FileText className="w-4 h-4" />
        HTML Preview
      </Button>

      <Button
        variant="outline"
        size="sm"
        onClick={exportToExcel}
        disabled={exporting || assets.length === 0}
        className="flex items-center gap-2"
      >
        <FileSpreadsheet className="w-4 h-4" />
        Excel
      </Button>

      {assets.length === 0 && (
        <span className="text-sm text-muted-foreground ml-auto">
          Add assets to enable export
        </span>
      )}

      {exporting && (
        <span className="text-sm text-primary ml-auto">
          Generating export...
        </span>
      )}
    </div>
  )
}

